import type { CRMUser, OpsCategory, OpsTask, OpsTaskOrigin, Subtask, TaskPriority, TaskStatus } from '../types';
import { filterOpsTaskEmployeeTeam } from './userRoles';

export type OpsAssigneeGroup = 'office_staff' | 'office_manager';

export const OPS_ASSIGNEE_GROUP_LABELS: Record<OpsAssigneeGroup, string> = {
  office_staff: 'Office staff',
  office_manager: 'Office manager',
};

export interface OpsTemplateLine {
  /** May include `{name}`. */
  title: string;
  assignTo: OpsAssigneeGroup;
}

export interface OpsTaskTemplateMeta {
  id: 'hiring';
  label: string;
  blurb: string;
}

export const OPS_TASK_TEMPLATES: OpsTaskTemplateMeta[] = [
  {
    id: 'hiring',
    label: 'Hiring',
    blurb: 'One task per person, with a checklist assigned to office staff and the office manager.',
  },
];

/** Starter hiring checklist. Edit titles here; `{name}` is filled in per person. */
export const HIRING_TEMPLATE_LINES: OpsTemplateLine[] = [
  { title: 'Confirm start date and role for {name}', assignTo: 'office_manager' },
  { title: 'Add {name} to payroll', assignTo: 'office_manager' },
  { title: 'Create email and CRM login for {name}', assignTo: 'office_staff' },
  { title: 'Handbook and training for {name}', assignTo: 'office_staff' },
  { title: 'Workspace and equipment for {name}', assignTo: 'office_staff' },
  { title: 'Add {name} to the org chart', assignTo: 'office_manager' },
];

export const HIRING_TEMPLATE_ORIGIN: OpsTaskOrigin = {
  kind: 'manual',
  label: 'Hiring template',
};

export interface BuiltOpsTaskDraft {
  title: string;
  description: string;
  category: OpsCategory;
  status: TaskStatus;
  priority: TaskPriority;
  assignedTo: string[];
  subtasks: Subtask[];
  origin: OpsTaskOrigin;
  dueDate?: Date;
  startDate?: Date;
}

export function resolveOpsAssigneeGroup(users: CRMUser[], group: OpsAssigneeGroup): CRMUser[] {
  const eligible = filterOpsTaskEmployeeTeam(users);
  const matched = eligible.filter((u) => {
    if (group === 'office_staff') return u.role === 'employee_office';
    return (u.designations || []).includes('office_manager');
  });
  return matched.sort((a, b) =>
    (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '', undefined, { sensitivity: 'base' }),
  );
}

function fillName(template: string, name: string): string {
  return template.split('{name}').join(name);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Replace a whole placeholder or previous name, leaving surrounding words intact. */
export function substitutePersonToken(text: string, from: string, to: string): string {
  if (!from || from === to || !text.includes(from)) return text;
  const re = new RegExp(`(^|[^A-Za-z0-9])${escapeRegExp(from)}(?=$|[^A-Za-z0-9])`, 'g');
  return text.replace(re, (_match, prefix: string) => `${prefix}${to}`);
}

/** Keep dates, assignees, and edited copy; only swap the person token. */
export function applyPersonNameToDraft(
  draft: BuiltOpsTaskDraft,
  from: string,
  to: string,
): BuiltOpsTaskDraft {
  if (!from || from === to) return draft;
  const title = substitutePersonToken(draft.title, from, to);
  const description = substitutePersonToken(draft.description, from, to);
  let linesChanged = false;
  const subtasks = draft.subtasks.map((line) => {
    const nextTitle = substitutePersonToken(line.title, from, to);
    if (nextTitle === line.title) return line;
    linesChanged = true;
    return { ...line, title: nextTitle };
  });
  if (title === draft.title && description === draft.description && !linesChanged) return draft;
  return { ...draft, title, description, subtasks };
}

/**
 * The saved task must use the person's name. Covers a draft that still
 * shows `{name}` because other fields were edited before the name was typed.
 */
export function finalizeHiringDraft(
  draft: BuiltOpsTaskDraft,
  name: string,
  appliedName?: string,
): BuiltOpsTaskDraft {
  const token = name.trim();
  if (!token) return draft;
  let next = applyPersonNameToDraft(draft, '{name}', token);
  if (appliedName && appliedName !== '{name}' && appliedName !== token) {
    next = applyPersonNameToDraft(next, appliedName, token);
  }
  return next;
}

function checklistForPerson(
  name: string,
  users: CRMUser[],
): { subtasks: Subtask[]; assignedTo: string[]; missingGroups: OpsAssigneeGroup[] } {
  const resolved = new Map<OpsAssigneeGroup, CRMUser[]>();
  const groups = new Set(HIRING_TEMPLATE_LINES.map((line) => line.assignTo));
  groups.forEach((group) => {
    resolved.set(group, resolveOpsAssigneeGroup(users, group));
  });

  const subtasks: Subtask[] = [];
  let position = 1;
  for (const line of HIRING_TEMPLATE_LINES) {
    const people = resolved.get(line.assignTo) || [];
    const title = fillName(line.title, name);
    if (people.length === 0) {
      subtasks.push({
        id: `hire_${position}`,
        title,
        completed: false,
        position,
      });
      position += 1;
      continue;
    }
    for (const person of people) {
      subtasks.push({
        id: `hire_${position}`,
        title,
        completed: false,
        position,
        assignedTo: person.uid,
      });
      position += 1;
    }
  }

  const assignedTo = Array.from(
    new Set(subtasks.map((s) => s.assignedTo).filter((uid): uid is string => !!uid)),
  );
  const missingGroups = (Array.from(groups) as OpsAssigneeGroup[]).filter(
    (group) => (resolved.get(group) || []).length === 0,
  );
  return { subtasks, assignedTo, missingGroups };
}

function hiringDescription(name: string, note: string | undefined, missingGroups: OpsAssigneeGroup[]): string {
  const parts = [`Hiring checklist for ${name}.`];
  const trimmed = note?.trim();
  if (trimmed) parts.push(trimmed);
  for (const group of missingGroups) {
    parts.push(
      `No ${OPS_ASSIGNEE_GROUP_LABELS[group].toLowerCase()} is on the team, so those checklist lines are unassigned.`,
    );
  }
  return parts.join('\n\n');
}

export function buildOpsTasksFromHiringTemplate(input: {
  names: string[];
  dueDate?: Date;
  note?: string;
  users: CRMUser[];
}): BuiltOpsTaskDraft[] {
  const names = input.names.map((n) => n.trim()).filter(Boolean);
  return names.map((name) => {
    const { subtasks, assignedTo, missingGroups } = checklistForPerson(name, input.users);
    const draft: BuiltOpsTaskDraft = {
      title: `Hire ${name}`,
      description: hiringDescription(name, input.note, missingGroups),
      category: 'admin',
      status: 'pending',
      priority: 'medium',
      assignedTo,
      subtasks,
      origin: HIRING_TEMPLATE_ORIGIN,
    };
    if (input.dueDate) draft.dueDate = input.dueDate;
    return draft;
  });
}

/** Fields `createTask` already understands, plus origin. */
export function hiringDraftToOpsTask(draft: BuiltOpsTaskDraft): Partial<OpsTask> {
  return {
    title: draft.title,
    description: draft.description,
    category: draft.category,
    status: draft.status,
    priority: draft.priority,
    assignedTo: draft.assignedTo,
    subtasks: draft.subtasks,
    origin: draft.origin,
    ...(draft.dueDate ? { dueDate: draft.dueDate } : {}),
    ...(draft.startDate ? { startDate: draft.startDate } : {}),
  };
}
